import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { apiClient } from '@indipia/shared'
import { Badge, Button, PhotoViewer } from '@indipia/ui-core'
import type { CustReqItem } from '@indipia/shared-features'
import { CUST_REQ_MESSAGE, formatReceivedAt, getRowView, photoCountText, statusBadgeTone } from '@indipia/shared-features'
import { ThumbImg } from '../ThumbImg'

// 고객 요청 한 줄 — 요약(상태 · 문장 · (사진 n)) → 누르면 전체 문장·사진 펼침. 접수 건의 [수정][삭제]는 접혀 있어도 요약 줄 오른쪽에 둔다.
// 사진은 썸네일(목록)과 원본(눌러서 크게 보기) 두 가지로 본다.

interface Props {
  item: CustReqItem
  onEdit: (item: CustReqItem) => void
  onDelete: (item: CustReqItem) => void
}

/** 인증이 필요한 원본 사진을 blob 으로 받아 objectURL 목록으로 만든다 — enabled 가 true 인 동안만 로드, 실패한 사진은 null */
function useOriginalBlobUrls(urls: string[], enabled: boolean): (string | null)[] | null {
  const [blobs, setBlobs] = useState<(string | null)[] | null>(null)
  const key = urls.join('|')
  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    const created: string[] = []
    Promise.all(urls.map(async u => {
      try {
        // ThumbImg 와 같이 게이트웨이 전체경로의 선행 /api 를 뗀다(apiClient.baseURL 이 이미 /api)
        const res = await apiClient.get<Blob>(u.replace(/^\/api(?=\/)/, ''), { responseType: 'blob', signal: controller.signal })
        const o = URL.createObjectURL(res.data)
        created.push(o)
        return o
      } catch { return null }
    })).then(list => { if (!controller.signal.aborted) setBlobs(list) })
    return () => {
      controller.abort()
      created.forEach(o => URL.revokeObjectURL(o))
      setBlobs(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key])
  return blobs
}

const caption: React.CSSProperties = { fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }

export function CustReqItemRow({ item, onEdit, onDelete }: Props) {
  const [open, setOpen] = useState(false)
  const [viewerIndex, setViewerIndex] = useState<number | null>(null)
  const view = getRowView(item)
  const originals = useOriginalBlobUrls(item.photos.map(p => p.url), viewerIndex !== null)

  const viewerItems = (originals ?? [])
    .map((src, i) => (src ? { id: item.photos[i].photoId, src, alt: `${item.photos[i].photoSeq}번 사진` } : null))
    .filter((v): v is { id: number; src: string; alt: string } => v !== null)
  // 원본을 하나도 받지 못하면 안내 후 닫는다
  const allFailed = viewerIndex !== null && originals !== null && viewerItems.length === 0
  useEffect(() => {
    if (!allFailed) return
    toast.error(CUST_REQ_MESSAGE.originalFail)
    setViewerIndex(null)
  }, [allFailed])
  const viewerStart = viewerIndex === null ? 0 : Math.max(0, viewerItems.findIndex(v => v.id === item.photos[viewerIndex]?.photoId))

  return (
    <li className="mb-2" style={{ listStyle: 'none' }}>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="flex-1 min-w-0 !justify-start"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          <span className="flex items-center gap-1.5 w-full min-w-0">
            <Badge status={statusBadgeTone(item.statusCode)}>{item.statusName}</Badge>
            {view.showUnsent && <Badge status="warning" title={CUST_REQ_MESSAGE.unsentTitle}>{CUST_REQ_MESSAGE.unsent}</Badge>}
            <span className="flex-1 min-w-0 truncate" style={{ textAlign: 'left' }}>{item.content}</span>
            {item.photos.length > 0 && <span style={caption}>{photoCountText(item.photos.length)}</span>}
          </span>
        </Button>
        {/* 접수 건의 [수정][삭제] — 펼침 버튼 안에 넣으면 버튼 중첩이 되므로 같은 줄 오른쪽에 나란히 둔다 */}
        {view.showEditActions && (
          <span className="flex items-center gap-1 shrink-0">
            <Button variant="secondary" size="sm" onClick={() => onEdit(item)}>{CUST_REQ_MESSAGE.edit}</Button>
            <Button variant="danger" size="sm" onClick={() => onDelete(item)}>{CUST_REQ_MESSAGE.remove}</Button>
          </span>
        )}
      </div>

      {open && (
        <div className="mt-1 px-2">
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface)' }}>{item.content}</div>
          <div className="mt-1" style={caption}>
            {[item.srcName, formatReceivedAt(item.receivedAt) && `${formatReceivedAt(item.receivedAt)} 접수`].filter(Boolean).join(' · ')}
          </div>
          {item.photos.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-2">
              {item.photos.map((p, i) => (
                <Button
                  key={p.photoId}
                  variant="ghost"
                  size="sm"
                  className="!p-0 !h-auto !min-w-0"
                  aria-label={`${CUST_REQ_MESSAGE.original}: ${p.photoSeq}번 사진`}
                  title={CUST_REQ_MESSAGE.original}
                  onClick={() => setViewerIndex(i)}
                >
                  <ThumbImg url={p.thumbUrl} className="w-16 h-16" alt={`${p.photoSeq}번 사진 썸네일`} />
                </Button>
              ))}
            </div>
          )}
        </div>
      )}

      {view.showAppliedNote && (
        <div className="mt-1 px-2" style={caption}>{CUST_REQ_MESSAGE.appliedNote}</div>
      )}

      {viewerIndex !== null && originals !== null && viewerItems.length > 0 && (
        <PhotoViewer
          open
          onClose={() => setViewerIndex(null)}
          items={viewerItems}
          index={viewerStart}
          onIndexChange={i => setViewerIndex(item.photos.findIndex(p => p.photoId === viewerItems[i]?.id))}
          title="고객 요청 사진"
        />
      )}
    </li>
  )
}
